<template>
  <div class="round-icon">
    <i class="bi bi-receipt" />
  </div>
  <div class="title-block" v-text="'Récapitulatif'" />
  <p>Pour rappel, une activation d’évaluation coûte 1 crédit par talent.</p>
  <div class="row">
    <p>Solde de crédits actuel</p>
    <p v-text="state.creditAtTheMoment.available" />
  </div>
  <div class="row">
    <p>Talents à activer</p>
    <p v-text="data.length" />
  </div>
  <div class="last row">
    <p :class="{ 'red-color': prevision < 0 }">
      Solde de crédits restant après activations
    </p>
    <p :class="{ 'red-color': prevision < 0 }" v-text="prevision" />
  </div>
  <Toast
    v-bind="prevision < 0 ? state.toasts[1] : state.toasts[0]"
    icon="bi-info-circle"
    :closable="false"
  />
  <div class="btn-container">
    <Button
      class="primary"
      :label="prevision < 0 ? 'Créditer mon solde' : 'Confirmer l\'activation'"
      :loading="state.btnIsLoading"
      @click="prevision >= 0 ? activateParticipants() : reloadCredits()"
    />
    <Button
      class="secondary outline"
      label="Annuler l'activation"
      @click="close"
    />
  </div>
</template>

<script setup>
import { useAppStore } from "@/stores/app.js";
import { reactive, computed, watch, inject } from "vue";
import { useRoute } from "vue-router";
import { useCompanyStore } from "@/stores/company.js";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import { useParticipantStore } from "@/stores/participant.js";

const emitter = inject("emitter");

const route = useRoute();

const emits = defineEmits(["close", "confirm"]);

const toast = useToast();

// Init stores
const participantStore = useParticipantStore();
const companyStore = useCompanyStore();
const appStore = useAppStore();

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
});

const state = reactive({
  toasts: [
    {
      title: "Pour information",
      text: "Lorsque vous confirmerez la dépense, les crédits nécessaires à l’activation des évaluations seront soustraits de votre solde. Si un talent ne termine pas son évaluation, le crédit soustrait vous sera remboursé.",
      color: "yellow",
    },
    {
      text: "Vous devez créditer votre solde pour activer les évaluations.",
      color: "red",
    },
  ],
  creditAtTheMoment: 0,
  btnIsLoading: false,
});

const prevision = computed(() => {
  return state.creditAtTheMoment.available - props.data.length;
});

function close() {
  emits("close");

  const item = JSON.parse(localStorage.getItem("pending_activations"));

  if (item && item.campaign_id === route.params.campaignId) {
    localStorage.removeItem("pending_activations");
  }
}

async function activateParticipants() {
  const item = JSON.parse(localStorage.getItem("pending_activations"));

  if (item && item.campaign_id === route.params.campaignId) {
    localStorage.removeItem("pending_activations");
  }

  state.btnIsLoading = true;

  const participant_ids = props.data[0].campaign
    ? [props.data[0].participant.participant_id]
    : props.data.map((elt) => elt.participant_id);

  const resp = await participantStore.activateParticipants(
    route.params.companyId,
    route.params.spaceId || props.data[0].campaign.workspace_id,
    route.params.campaignId || props.data[0].campaign.campaign_id,
    participant_ids
  );

  if (resp.code) {
    toast.error(getError(resp.key));
  } else {
    toast.success(
      `Candidat${props.data.length > 1 ? "s" : ""} activé${
        props.data.length > 1 ? "s" : ""
      } avec succès.`
    );

    if (props.data.length > 1) emitter.emit("confirmBulk");

    appStore.activatedTableHasNews[`${route.params.campaignId}`] = true;
  }

  emits("confirm");

  state.btnIsLoading = true;
}

function reloadCredits() {
  emits("close");

  appStore.openPaymentModal("activation_modal");

  if (appStore.paymentModalIsOpen) {
    appStore.creditsNeeded =
      props.data.length - companyStore.currentCompany.credits.available;

    if (route.name === "campaign" || route.name === "addCandidates") {
      localStorage.setItem(
        "pending_activations",
        JSON.stringify({
          space_id: route.params.spaceId,
          campaign_id: route.params.campaignId,
          participants: props.data,
        })
      );
    }
  }

}

watch(
  () => props.data,
  () => {
    state.creditAtTheMoment = { ...companyStore.currentCompany.credits };
  },
  { immediate: true }
);
</script>
